<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import axios from 'axios'
import html2pdf from 'html2pdf.js'

export default {
  page: {
    title: 'Tenable Assets with Vulnerabilities',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader
  },
  data () {
    return {
      updated: '',
      vulnerabilities: [],
      filteredVulnerabilities: [],
      targetGroups: [],
      title: 'Tenable Assets with Vulnerabilities',
      loading: true,
      // Search filters
      filters: {
        asset: ''
      },
      // Selected severity levels for multi-select
      selectedSeverities: [],
      // Selected labels for multi-select
      selectedLabels: [],
      // Severity options for checkboxes
      severityOptions: [
        { value: 'Critical', text: 'Critical' },
        { value: 'High', text: 'High' },
        { value: 'Medium', text: 'Medium' },
        { value: 'Low', text: 'Low' },
        { value: 'Info', text: 'Info' }
      ],
      // Date range selection
      startDate: null,
      endDate: null,
      // Sorting
      sortKey: '',
      sortDirection: 'asc',
      // Modal state and data
      showAssetVulnModal: false,
      selectedAsset: null,
      assetVulnerabilities: [],
      loadingAssetVulns: false,
      items: [
        {
          text: 'Home',
          href: '/'
        },
        {
          text: 'Tenable',
          href: '/tenable'
        },
        {
          text: 'Vulnerabilities',
          active: true
        }
      ],
      // Column headers for the table
      columns: [
        {
          key: 'fqdn',
          label: 'FQDN',
          sortable: true
        },
        {
          key: 'ipv4',
          label: 'IPv4',
          sortable: true
        },
        {
          key: 'labels',
          label: 'Labels',
          sortable: true
        },
        {
          key: 'severities',
          label: 'Severities',
          sortable: false
        },
        {
          key: 'total',
          label: 'Total',
          sortable: true
        },
        {
          key: 'last_seen',
          label: 'Last Seen',
          sortable: true
        }
      ],
      // Vulnerability detail columns for modal
      vulnColumns: [
        { key: 'plugin_name', label: 'Name', sortable: true },
        { key: 'family', label: 'Family', sortable: true },
        { key: 'severity', label: 'Severity', sortable: true },
        { key: 'first_found', label: 'First Found', sortable: true },
        { key: 'last_found', label: 'Last Found', sortable: true }
      ],
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      pdfConfig: {
        margin: 0.2,
        filename: 'Tenable Scan Report - Corona.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, dpi: 96, letterRendering: true, width: 1280 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
        pagebreak: { mode: 'css', before: '#page2el' },
        _pageBreak: { mode: 'avoid-all', before: '.break-page', after: '.break-page' }
      }
    }
  },
  computed: {
    // Get all unique labels from target groups
    allLabels () {
      if (!this.targetGroups || !Array.isArray(this.targetGroups)) {
        console.log('No target groups available:', this.targetGroups)
        return []
      }

      console.log('Target groups raw data:', this.targetGroups)
      console.log('Target groups length:', this.targetGroups.length)
      const labels = this.targetGroups.map(group => {
        console.log('Processing group:', group)
        return group.name
      }).filter(name => name && name !== 'Default')
      const uniqueLabels = [...new Set(labels)].sort()
      console.log('Extracted labels:', labels)
      console.log('Unique labels:', uniqueLabels)
      // Return only the actual target group labels
      const result = [...uniqueLabels]
      console.log('Final allLabels result:', result)
      return result
    },
    sortedAssetVulnerabilities () {
      if (!this.assetVulnerabilities) return []

      // First filter by selected severities
      let filtered = this.assetVulnerabilities
      if (this.selectedSeverities.length > 0) {
        filtered = filtered.filter(vuln => {
          const severityText = this.getSeverityText(vuln.severity)
          return this.selectedSeverities.includes(severityText)
        })
      }

      const severityOrder = {
        'Critical': 5,
        'High': 4,
        'Medium': 3,
        'Low': 2,
        'Info': 1,
        'Unknown': 0
      }

      return [...filtered].sort((a, b) => {
        const severityA = this.getSeverityText(a.severity)
        const severityB = this.getSeverityText(b.severity)
        // Higher number means higher priority (Critical = 5 will come first)
        return (severityOrder[severityB] || 0) - (severityOrder[severityA] || 0)
      })
    }
  },

  watch: {
    // Watch for changes in filters and update filtered data
    filters: {
      handler () {
        this.applyFilters()
      },
      deep: true
    },
    // Watch for changes in selected labels
    selectedLabels: {
      handler () {
        this.applyFilters()
      }
    },
    // Update filtered data when raw data changes
    vulnerabilities: {
      handler () {
        this.applyFilters()
      },
      immediate: true
    },
    // Reload data when severity selection changes
    selectedSeverities: {
      handler () {
        this.loadData()
      }
    },
    // Reload data when start date changes
    startDate: {
      handler () {
        this.loadData()
      }
    },
    // Reload data when end date changes
    endDate: {
      handler () {
        this.loadData()
      }
    }
  },
  mounted () {
    // Select all severities by default
    this.selectedSeverities = ['Critical']
    // Set default date range to last 7 days
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(endDate.getDate() - 7)
    this.endDate = endDate.toISOString().split('T')[0]
    this.startDate = startDate.toISOString().split('T')[0]
    this.loadData()
  },
  methods: {
    loadData () {
      let that = this
      let baseURL = that.appConfig.xdrAPI.baseURL || 'http://localhost:5000'

      // Load targets-groups

      const urlTG = `${baseURL}/tenable/target-groups`

      axios.get(urlTG)
        .then((response) => {
          console.log('target groups', response)
          // Check if response.data exists
          if (response.data) {
            that.targetGroups = response.data.target_groups || []
            // Set default selection to all labels on first load
            if (that.selectedLabels.length === 0) {
              that.$nextTick(() => {
                that.selectedLabels = [...that.allLabels]
              })
            }
          }
        })
        .catch(function (error) {
          console.error('Error fetching target groups:', error)
          that.vulnerabilities = []
          that.loading = false
        })

      // Build params object
      let params = {}
      // Add date range if both dates are selected
      if (this.startDate && this.endDate) {
        const start = new Date(this.startDate)
        const end = new Date(this.endDate)
        const diffTime = Math.abs(end - start)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        params['date_range'] = diffDays.toString()
      }

      // Add severity filters if any are selected
      if (this.selectedSeverities.length > 0) {
        params['filter.0.filter'] = 'severity'
        params['filter.0.quality'] = 'match'
        params['filter.0.value'] = this.selectedSeverities.join(',')
      } else {
        // If nothing selected, don't apply severity filter
        // This allows all severities to be shown
      }

      // Convert params to URL query string
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')

      const url = `${baseURL}/tenable/assets/vulnerabilities?${queryString}`

      that.loading = true

      axios.get(url)
        .then((response) => {
          console.log('response vulnerabilities', response)
          // Check if response.data exists and has the expected format
          if (response.data && Array.isArray(response.data)) {
            that.vulnerabilities = response.data
          } else if (response.data && response.data.assets && Array.isArray(response.data.assets)) {
            // Handle case where API returns data in a nested property
            that.vulnerabilities = response.data.assets
          } else {
            console.error('Unexpected API response format:', response.data)
            that.vulnerabilities = []
          }
          const now = new Date()
          that.updated = now.toLocaleString()
          that.loading = false
          that.applyFilters() // Apply filters to the loaded data
        })
        .catch(function (error) {
          console.error('Error fetching vulnerability data:', error)
          that.vulnerabilities = []
          that.loading = false
        })
    },
    // Extract labels from target groups using IPv4 as members key
    labelsFromTargetGroups (ipv4) {
      let labels = []
      if (!this.targetGroups || !Array.isArray(this.targetGroups)) return labels
      this.targetGroups.forEach(group => {
        if (!group.members) return
        const members = group.members.split(', ') || []
        if (members && Array.isArray(members) && members.includes(ipv4)) {
          labels.push(group.name)
        }
      })
      // Debug when filtering by labels
      if (this.selectedLabels.length > 0) {
        console.log(`Labels for IP ${ipv4}:`, labels)
      }
      return labels
    },
    // Format the date from timestamp
    formatDate (timestamp) {
      if (!timestamp) return 'N/A'

      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    },
    // Get severity class for badge styling
    getSeverityClass (severity) {
      if (severity === undefined || severity === null) return 'badge bg-secondary'

      // Convert to number if it's a string
      const severityNum = typeof severity === 'string' ? parseInt(severity, 10) : severity

      switch (severityNum) {
        case 4:
          return 'badge bg-danger'
        case 3:
          return 'badge bg-warning'
        case 2:
          return 'badge bg-info'
        case 1:
          return 'badge bg-success'
        case 0:
          return 'badge bg-secondary'
        default:
          return 'badge bg-secondary'
      }
    },
    // Get severity text
    getSeverityText (severity) {
      if (severity === undefined || severity === null) return 'Unknown'

      // If it's already a string that matches our known severities, just return it
      if (typeof severity === 'string') {
        const normalizedSeverity = severity.trim().toLowerCase()
        if (['critical', 'high', 'medium', 'low', 'info', 'information'].includes(normalizedSeverity)) {
          // Capitalize first letter
          return normalizedSeverity.charAt(0).toUpperCase() + normalizedSeverity.slice(1)
        }

        // Try to parse as number if it's a numeric string
        if (!isNaN(severity)) {
          severity = parseInt(severity, 10)
        } else {
          return 'Unknown'
        }
      }

      // Handle numeric severity levels
      switch (severity) {
        case 4:
          return 'Critical'
        case 3:
          return 'High'
        case 2:
          return 'Medium'
        case 1:
          return 'Low'
        case 0:
          return 'Info'
        default:
          return 'Unknown'
      }
    },
    // Get color class for a specific severity name
    getSeverityColorClass (severityName) {
      // Check if severityName is a string, if not return default
      if (!severityName || typeof severityName !== 'string') {
        return 'text-secondary'
      }

      switch (severityName.toLowerCase()) {
        case 'critical':
          return 'text-danger'
        case 'high':
          return 'text-warning'
        case 'medium':
          return 'text-info'
        case 'low':
          return 'text-success'
        case 'info':
        case 'information':
          return 'text-secondary'
        default:
          return 'text-secondary'
      }
    },
    // Sort severities in the correct order
    sortSeverities (severities) {
      const order = {
        'Critical': 1,
        'High': 2,
        'Medium': 3,
        'Low': 4,
        'Information': 5,
        'Info': 5 // Alias for Information
      }

      return [...severities].sort((a, b) => {
        return (order[a.name] || 99) - (order[b.name] || 99)
      })
    },

    // Apply filters to the vulnerability data
    applyFilters () {
      let result = [...this.vulnerabilities]

      // Filter by asset (FQDN or IPv4)
      if (this.filters.asset) {
        const assetSearch = this.filters.asset.toLowerCase()
        result = result.filter(item => {
          // Check if any FQDN contains the search term
          const fqdnMatch = item.fqdn && item.fqdn.some(fqdn =>
            fqdn.toLowerCase().includes(assetSearch)
          )

          // Check if any IPv4 contains the search term
          const ipMatch = item.ipv4 && item.ipv4.some(ip =>
            ip.toLowerCase().includes(assetSearch)
          )

          return fqdnMatch || ipMatch
        })
      }

      // Filter by selected severity levels
      if (this.selectedSeverities.length > 0) {
        result = result.filter(item => {
          return item.severities && item.severities.some(sev =>
            this.selectedSeverities.includes(sev.name) && sev.count > 0
          )
        })
      }

      // Filter by selected labels
      if (this.selectedLabels.length > 0) {
        result = result.filter(item => {
          if (!item.ipv4 || !Array.isArray(item.ipv4)) return false
          return this.selectedLabels.some(selectedLabel => {
            // Show assets that have the selected label
            return item.ipv4.some(ip => {
              const assetLabels = this.labelsFromTargetGroups(ip)
              return assetLabels.includes(selectedLabel)
            })
          })
        })
      }

      // Apply sorting if a sort key is set
      if (this.sortKey) {
        result = this.sortData(result, this.sortKey, this.sortDirection)
      }

      this.filteredVulnerabilities = result
    },

    // Sort the data by a specific key and direction
    sortData (data, key, direction) {
      return [...data].sort((a, b) => {
        let aValue, bValue

        // Handle special cases for different column types
        if (key === 'fqdn') {
          // Sort by the first FQDN in the list
          aValue = a.fqdn && a.fqdn.length > 0 ? a.fqdn[0].toLowerCase() : ''
          bValue = b.fqdn && b.fqdn.length > 0 ? b.fqdn[0].toLowerCase() : ''
        } else if (key === 'ipv4') {
          // Sort by the first IP in the list
          aValue = a.ipv4 && a.ipv4.length > 0 ? a.ipv4[0] : ''
          bValue = b.ipv4 && b.ipv4.length > 0 ? b.ipv4[0] : ''
        } else if (key === 'total') {
          // Sort numerically
          aValue = a.total || 0
          bValue = b.total || 0
        } else if (key === 'last_seen') {
          // Sort by date string
          aValue = a.last_seen || ''
          bValue = b.last_seen || ''
        } else {
          // Default sorting for other columns
          aValue = a[key] || ''
          bValue = b[key] || ''
        }

        // Perform the actual comparison
        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })
    },

    // Toggle sorting when a column header is clicked
    toggleSort (key) {
      // Only allow sorting on sortable columns
      const column = this.columns.find(col => col.key === key)
      if (!column || !column.sortable) return

      // If clicking the same column, toggle direction
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        // New column, set as ascending
        this.sortKey = key
        this.sortDirection = 'asc'
      }

      // Apply the new sorting
      this.applyFilters()
    },

    // Get the sort icon for a column
    getSortIcon (key) {
      if (this.sortKey !== key) return 'mdi mdi-sort'
      return this.sortDirection === 'asc' ? 'mdi mdi-sort-ascending' : 'mdi mdi-sort-descending'
    },

    // Open modal and load asset vulnerabilities
    openAssetVulnerabilities (asset) {
      this.selectedAsset = asset
      this.showAssetVulnModal = true
      this.loadAssetVulnerabilities(asset.id)
    },

    // Close the asset vulnerabilities modal
    closeAssetVulnModal () {
      this.showAssetVulnModal = false
      this.selectedAsset = null
      this.assetVulnerabilities = []
    },

    // Load vulnerabilities for a specific asset
    loadAssetVulnerabilities (assetId) {
      this.loadingAssetVulns = true
      this.assetVulnerabilities = []

      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const url = `${baseURL}/tenable/asset/${assetId}/vulnerabilities`

      axios.get(url)
        .then(response => {
          console.log('Asset vulnerabilities response:', response)

          try {
            // Check if the response has the expected format
            if (response.data && Array.isArray(response.data)) {
              this.assetVulnerabilities = response.data
            } else if (response.data && response.data.vulnerabilities && Array.isArray(response.data.vulnerabilities)) {
              this.assetVulnerabilities = response.data.vulnerabilities
            } else {
              console.error('Unexpected API response format for asset vulnerabilities:', response.data)
              this.assetVulnerabilities = []
            }

            console.log('Loaded asset vulnerabilities:', this.assetVulnerabilities)
            // Process vulnerabilities to ensure data consistency
            this.assetVulnerabilities = this.assetVulnerabilities.map(vuln => {
              // Make sure severity is in the correct format
              if (vuln.severity !== undefined && vuln.severity !== null) {
                // Keep as is, getSeverityText will handle conversion
              } else {
                vuln.severity = 'Unknown'
              }
              return vuln
            })
          } catch (err) {
            console.error('Error processing vulnerability data:', err)
            this.assetVulnerabilities = []
          }

          this.loadingAssetVulns = false
        })
        .catch(error => {
          console.error('Error fetching asset vulnerabilities:', error)
          this.assetVulnerabilities = []
          this.loadingAssetVulns = false
        })
    },

    // Format a timestamp for display
    formatFullDate (timestamp) {
      if (!timestamp) return 'N/A'
      return timestamp
      // try {
      //   let date
      //   if (typeof timestamp === 'number') {
      //     // Always treat as milliseconds since API sends timestamps in ms
      //     date = new Date(timestamp)
      //   } else if (typeof timestamp === 'string') {
      //     if (timestamp.match(/^\d+$/)) {
      //       // String containing only numbers - treat as milliseconds
      //       date = new Date(parseInt(timestamp, 10))
      //     } else {
      //       // Try to parse as ISO string or other date format
      //       date = new Date(timestamp)
      //     }
      //   } else {
      //     return 'N/A'
      //   }

      //   if (isNaN(date.getTime())) {
      //     console.error('Invalid date:', timestamp)
      //     return 'N/A'
      //   }

      //   // Use Intl.DateTimeFormat for consistent formatting
      //   return new Intl.DateTimeFormat('default', {
      //     year: 'numeric',
      //     month: 'short',
      //     day: '2-digit',
      //     hour: '2-digit',
      //     minute: '2-digit',
      //     hour12: false,
      //     timeZoneName: 'short'
      //   }).format(date)
      // } catch (err) {
      //   console.error('Error formatting date:', err, timestamp)
      //   return 'N/A'
      // }
    },
    downloadPdf () {
      const element = document.getElementById('pdfcontent')
      html2pdf(element, this.pdfConfig)
    },
    downloadPdfModal () {
      const element = document.getElementById('pdfcontentmodal')
      html2pdf(element, this.pdfConfig)
    },

    // Format date for display
    formatDisplayDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },

    // Export table data to CSV
    exportTableToCSV () {
      // Build headers from columns
      const headers = this.columns.map(col => col.label).join(',')

      // Transform data rows
      const rows = this.filteredVulnerabilities.map(vuln => {
        return [
          vuln.fqdn ? `"${vuln.fqdn.join('; ')}"` : '',
          vuln.ipv4 ? `"${vuln.ipv4.join('; ')}"` : '',
          vuln.ipv4 ? `"${this.labelsFromTargetGroups(vuln.ipv4[0]).join('; ')}"` : '',
          `"${vuln.severities.filter(s => s.count > 0).map(s => `${s.name}: ${s.count}`).join('; ')}"`,
          vuln.total || '0',
          vuln.last_seen || ''
        ].join(',')
      })

      // Combine headers and rows
      const csv = [headers, ...rows].join('\n')

      // Download the CSV file
      const dateRange = this.startDate && this.endDate ? `${this.startDate}_to_${this.endDate}` : 'all_time'
      this.downloadCSV(csv, `vulnerabilities_${dateRange}.csv`)
    },

    // Helper function to download CSV
    downloadCSV (csv, filename) {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, filename)
      } else {
        // Other browsers
        const url = URL.createObjectURL(blob)
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader
      :title="title"
      :items="items"
    />
    <div
      class="container"
      style="max-width: 1420px !important;"
    >
      <div class="row page-title align-items-center" />
      <div class="col-12">
        <b-button
          class="btn btn-primary btn-sm mr-1"
          variant="light"
          @click="downloadPdf"
        >
          Download PDF
        </b-button>
        <b-button
          class="btn btn-primary btn-sm mr-1"
          variant="light"
          @click="exportTableToCSV"
        >
          <i class="mdi mdi-download mr-1" />Export CSV
        </b-button>
        <br><br>
        <div
          id="pdfcontent"
          style="width:1420px; break-after: page; clear: both;padding-left:20px;padding-right:20px;"
        >
          <section slot="pdf-content">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <h4 class="header-title">
                    Assets with
                    <span v-if="selectedSeverities.length === 0">All</span>
                    <span v-else-if="selectedSeverities.length === severityOptions.length">All</span>
                    <span v-else>
                      {{ selectedSeverities.join(', ') }}
                    </span>
                    Vulnerabilities
                    <span v-if="startDate && endDate">
                      from {{ formatDisplayDate(startDate) }} to {{ formatDisplayDate(endDate) }}
                    </span>
                    <span v-else>All Time</span>
                  </h4>
                  <span>Updated: {{ updated }}</span>
                </div>

                <!-- Search and filter controls -->
                <div class="row mb-2">
                  <div class="col-md-2">
                    <div class="form-group">
                      <label class="form-label">Search Assets (FQDN/IP)</label>
                      <input
                        v-model="filters.asset"
                        type="text"
                        class="form-control"
                        placeholder="Search by hostname or IP"
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label class="form-label">Start Date</label>
                          <input
                            v-model="startDate"
                            type="date"
                            class="form-control"
                          >
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label class="form-label">End Date</label>
                          <input
                            v-model="endDate"
                            type="date"
                            class="form-control"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="form-label">Labels ({{ allLabels.length }})</label>
                      <div style="border: 1px solid #ccc; height: 70px; overflow-y: auto; padding: 5px;">
                        <div
                          v-for="label in allLabels"
                          :key="label"
                          class="form-check"
                        >
                          <input
                            :id="`label-${label}`"
                            v-model="selectedLabels"
                            type="checkbox"
                            :value="label"
                            class="form-check-input"
                          >
                          <label
                            :for="`label-${label}`"
                            class="form-check-label"
                          >
                            {{ label }}
                          </label>
                        </div>
                      </div>
                      <small
                        v-if="allLabels.length === 1"
                        class="text-muted"
                      >
                        No labels available
                      </small>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="form-label mb-2">Filter by Severity</label>
                      <div class="severity-checkboxes d-flex flex-nowrap align-items-center">
                        <div
                          v-for="option in severityOptions"
                          :key="option.value"
                          class="form-check form-check-inline me-2"
                        >
                          <input
                            :id="`severity-${option.value}`"
                            v-model="selectedSeverities"
                            type="checkbox"
                            :value="option.value"
                            class="form-check-input"
                          >
                          <label
                            :for="`severity-${option.value}`"
                            class="form-check-label"
                            :class="getSeverityColorClass(option.value)"
                          >
                            {{ option.text }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Loading message -->
                <div
                  v-if="loading"
                  class="text-center py-5"
                >
                  <p class="mt-2">
                    Loading vulnerabilities...
                  </p>
                </div>

                <!-- Table content (only shown when not loading) -->
                <div
                  v-else
                  class="table-responsive"
                >
                  <!-- Debug info - can be removed after fixing -->
                  <div
                    v-if="vulnerabilities.length === 0"
                    class="alert alert-info mb-3"
                  >
                    No vulnerability data received from API. Please check the console for more details.
                  </div>

                  <div
                    v-else-if="filteredVulnerabilities.length === 0"
                    class="alert alert-warning mb-3"
                  >
                    No vulnerabilities match your search criteria. Try adjusting your filters.
                  </div>

                  <div style="display: flex;justify-content: center;">
                    <table class="table table-centered table-hover mb-0">
                      <thead>
                        <tr>
                          <th
                            v-for="column in columns"
                            :key="column.key"
                            :class="{ 'sortable': column.sortable, 'active': sortKey === column.key }"
                            @click="column.sortable && toggleSort(column.key)"
                          >
                            {{ column.label }}
                            <i
                              v-if="column.sortable"
                              :class="getSortIcon(column.key)"
                              class="sort-icon"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(vuln, index) in filteredVulnerabilities"
                          :key="index"
                        >
                          <td>
                            <ul>
                              <li
                                v-for="fqdn in vuln.fqdn"
                                :key="fqdn"
                              >
                                {{ fqdn }}
                              </li>
                            </ul>
                          </td>
                          <td>
                            <ul>
                              <li
                                v-for="ip in vuln.ipv4"
                                :key="ip"
                              >
                                {{ ip }}
                              </li>
                            </ul>
                          </td>
                          <td>
                            <ul class="list-unstyled mb-0">
                              <template v-for="ip in vuln.ipv4">
                                <li
                                  v-for="label in labelsFromTargetGroups(ip)"
                                  :key="label"
                                >
                                  {{ label }}
                                </li>
                              </template>
                            </ul>
                          </td><td>
                            <ul class="list-unstyled mb-0">
                              <li
                                v-for="severity in sortSeverities(vuln.severities)"
                                :key="severity.name"
                                :class="getSeverityColorClass(severity.name)"
                              >
                                <template v-if="severity.count > 0">
                                  <i class="mdi mdi-circle-small" /> {{ severity.name }}: {{ severity.count }}
                                </template>
                              </li>
                            </ul>
                          </td>
                          <td>{{ vuln.total }}</td>
                          <td>{{ vuln.last_seen }}</td>
                          <td>
                            <button
                              class="btn btn-sm btn-primary"
                              title="View Vulnerabilities"
                              @click="openAssetVulnerabilities(vuln)"
                            >
                              <i class="mdi-shield-bug-outline">Details</i>
                            </button>
                          </td>
                        </tr>
                        <tr v-if="filteredVulnerabilities.length === 0">
                          <td
                            :colspan="columns.length + 1"
                            class="text-center"
                          >
                            No vulnerabilities found
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Asset Vulnerabilities Modal -->
    <div
      v-if="showAssetVulnModal"
      class="modal fade"
      style="width:1420px;"
      :class="{ 'show d-block': showAssetVulnModal }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div
            id="pdfcontentmodal"
            style="width:1280px; break-after: page; clear: both;padding-left:20px;padding-right:20px;"
          >
            <section slot="pdf-content-modal" />
            <div class="modal-header">
              <h5 class="modal-title">
                Vulnerabilities for Asset
                <span
                  v-if="selectedAsset && selectedAsset.fqdn && selectedAsset.fqdn.length > 0"
                  class="fw-bold"
                >
                  {{ selectedAsset.fqdn[0] }}
                </span>
                <span
                  v-else-if="selectedAsset && selectedAsset.ipv4 && selectedAsset.ipv4.length > 0"
                  class="fw-bold"
                >
                  {{ selectedAsset.ipv4[0] }}
                </span>
                <span>&nbsp;-&nbsp;Updated: {{ updated }}</span>
              </h5>
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                @click="closeAssetVulnModal"
              />
            </div>
            <div class="modal-body">
              <!-- Loading state -->
              <div
                v-if="loadingAssetVulns"
                class="text-center p-4"
              >
                <p class="mb-0">
                  Loading vulnerabilities...
                </p>
              </div>

              <!-- Error state -->
              <div
                v-else-if="assetVulnerabilities.length === 0"
                class="alert alert-info mb-0"
              >
                No vulnerabilities found for this asset.
              </div>

              <!-- Vulnerabilities table -->
              <div
                v-else
                class="table-responsive"
              >
                <table class="table table-striped table-centered mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Family</th>
                      <th>Severity</th>
                      <th>First Found</th>
                      <th>Last Found</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(vuln, index) in sortedAssetVulnerabilities"
                      :key="index"
                    >
                      <td class="text-wrap">
                        {{ vuln.plugin_name || 'N/A' }}
                      </td>
                      <td>{{ vuln.plugin_family || 'N/A' }}</td>
                      <td>
                        <span :class="getSeverityColorClass(getSeverityText(vuln.severity))">
                          {{ getSeverityText(vuln.severity) }}
                        </span>
                      </td>
                      <td>{{ formatFullDate(vuln.first_found) }}</td>
                      <td>{{ formatFullDate(vuln.last_found) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="downloadPdfModal"
            >
              Download PDF
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeAssetVulnModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="showAssetVulnModal"
        class="modal-backdrop fade show"
      />
    </div>
  </Layout>
</template>

<style scoped>
.table th {
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

/* Sortable column styles */
.sortable {
  cursor: pointer;
  position: relative;
  user-select: none;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.sortable.active {
  background-color: rgba(0, 0, 0, 0.075);
}

.sort-icon {
  margin-left: 5px;
}

/* Filter controls */
.form-group {
  margin-bottom: 1rem;
}

/* Table styles for compact rows */
.table td, .table th {
  padding: 0.5rem;
  line-height: 1.2;
}

.table td ul {
  margin-bottom: 0;
}

.table td ul li {
  line-height: 1.2;
  margin-bottom: 0.1rem;
}

.table td ul li:last-child {
  margin-bottom: 0;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

.severity-checkboxes {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.form-check-inline {
  margin-right: 0.5rem;
  white-space: nowrap;
}

.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 1040 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  opacity: 1 !important;
}

.modal.show {
  display: block;
  z-index: 1050;
}

.modal-dialog {
  position: relative;
  width: auto;
  margin: 0.5rem;
  pointer-events: none;
  z-index: 1060;
}

.modal.show .modal-dialog {
  transform: none;
}

.modal-xl {
  max-width: 90%;
  margin: 1.75rem auto;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.3rem;
  outline: 0;
}

.text-wrap {
  white-space: normal;
  word-break: break-word;
}

/* Modal styles */
.modal-content {
  border-radius: 0.5rem;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
}
</style>
